import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ExportToPSTEvents } from '@common/models';
import { DownloadService } from '@common/services/download.service';
import { TemplateRegistryService, TemplateType } from '@common/services/template-registry.service';
import { getErrorText, hasErrorResponseText } from '@common/utils';
import { I18NextPipe } from 'angular-i18next';
import { ToastService } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { filter, finalize, take } from 'rxjs/operators';

interface ExportToCSVOptions {
  loadingFn: (val: boolean) => void;
  fileName: string;
}

interface ExportToPSTOptions {
  loadingFn: (val: boolean) => void;
}

@Injectable()
export class ExportToService {
  private readonly toastTitleSuccess = this.i18nPipe.transform('toast.success.title', { format: 'title' });
  private readonly toastTitleError = this.i18nPipe.transform('toast.error.title', { format: 'title' });

  constructor(
    private downloadService: DownloadService,
    private i18nPipe: I18NextPipe,
    private templateRegistry: TemplateRegistryService,
    private toastService: ToastService
  ) {}

  toCSV(exportFn: Observable<string | Blob>, { loadingFn, fileName }: ExportToCSVOptions): void {
    loadingFn(true);
    exportFn
      .pipe(
        take(1),
        filter((file): file is Blob => file instanceof Blob),
        finalize(() => loadingFn(false))
      )
      .subscribe({
        next: (file) => this.downloadService.saveAs(file, this.downloadService.generateFileName(fileName)),
        error: (res: HttpErrorResponse) => this.errorHandler(res)
      });
  }

  toPST(exportFn: Observable<ExportToPSTEvents>, { loadingFn }: ExportToPSTOptions): void {
    const successTemplate = this.templateRegistry.getTemplate(TemplateType.Export);

    loadingFn(true);
    exportFn
      .pipe(
        take(1),
        finalize(() => loadingFn(false))
      )
      .subscribe({
        next: () => this.toastService.success(successTemplate, this.toastTitleSuccess),
        error: (res: HttpErrorResponse) => this.errorHandler(res)
      });
  }

  private errorHandler(res: HttpErrorResponse): void {
    hasErrorResponseText(res) && this.toastService.error(getErrorText(res), this.toastTitleError);
  }
}
